import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { COMPANY } from "@/data/site";
import { useInView } from "@/hooks/useReveal";
import FeaturedProject from "@/components/FeaturedProject";

const STATS = [
  { value: "Saját brigád", label: "nincs alvállalkozó" },
  { value: "Írásos garancia", label: "minden munkára" },
  { value: "Ingyenes felmérés", label: "kötelezettség nélkül" },
  { value: "Tételes ajánlat", label: "átlátható árazás" },
];

function Stat({ s, i }) {
  return (
    <div
      data-testid={`stat-${i}`}
      style={{
        padding: "20px 24px 20px 0",
        borderRight: i < 3 ? "1px solid var(--line)" : "none",
        paddingLeft: i === 0 ? 0 : 24,
      }}
    >
      <div className="font-display" style={{
        fontSize: "clamp(1.1rem, 1.6vw, 1.35rem)",
        color: "var(--text)", lineHeight: 1.15, letterSpacing: "-0.02em", fontWeight: 500,
      }}>
        {s.value}
      </div>
      <div className="small" style={{ marginTop: 8, color: "var(--text-dim)" }}>
        {s.label}
      </div>
    </div>
  );
}

export default function Hero() {
  const [heroRef, heroInView] = useInView();
  const [statsRef] = useInView({ threshold: 0.4, once: true });

  return (
    <section
      id="hero"
      ref={heroRef}
      data-testid="hero"
      style={{
        background: "var(--ink)",
        color: "var(--text)",
        paddingTop: 140, paddingBottom: 56,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Photo background */}
      <div className="hero-photo-bg" aria-hidden />
      <div className="hero-photo-overlay" aria-hidden />

      {/* Background decor dots */}
      <span className="bg-dot pulse" style={{ top: "22%", right: "8%", animationDelay: "0s" }} aria-hidden />
      <span className="bg-dot pulse" style={{ top: "65%", right: "16%", animationDelay: "0.8s", opacity: 0.5 }} aria-hidden />
      <span className="bg-dot pulse" style={{ top: "78%", left: "6%", animationDelay: "1.4s", opacity: 0.6 }} aria-hidden />

      <div className="site-container" style={{ position: "relative", zIndex: 2 }}>
        <div className={`reveal ${heroInView ? "in-view" : ""}`} style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 48 }}>
          <span style={{ height: 1, background: "var(--gold)", width: 32 }} />
          <span className="eyebrow" data-testid="hero-eyebrow">
            01 · Totál-Bádogos Kft.
          </span>
        </div>

        <div
          className="hero-grid"
          style={{ display: "grid", gridTemplateColumns: "1fr", gap: 48, alignItems: "end" }}
        >
          <style>{`
            @media (min-width: 1024px) {
              .hero-grid { grid-template-columns: 1.4fr 1fr !important; gap: 64px !important; }
            }
          `}</style>

          <div className={`reveal reveal-1 ${heroInView ? "in-view" : ""}`} style={{ maxWidth: 880 }}>
            <h1 className="h1" data-testid="hero-h1" style={{ margin: 0 }}>
              Bádogos és tetőfedő munkák, <span style={{ color: "var(--gold)" }}>komolyan véve.</span>
            </h1>
            <p data-testid="hero-body" className="lead" style={{ marginTop: 26, maxWidth: 600, fontSize: 16 }}>
              Bádogos szerkezetek, korcolt lemezfedés, tetőszerkezet és felújítás — precíz kézi munka, saját brigád és írásos garancia.
            </p>

            <div style={{ marginTop: 32, display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Link to="/kapcsolat" className="btn btn-gold" data-testid="hero-cta-primary">
                <span>Ingyenes felmérés</span>
                <ArrowRight className="btn-arrow" size={14} />
              </Link>
              <a href="/#referenciak" className="btn btn-ghost" data-testid="hero-cta-secondary">
                <span>Munkáim megtekintése</span>
              </a>
            </div>
          </div>

          <div className={`reveal reveal-2 ${heroInView ? "in-view" : ""}`}>
            <FeaturedProject />
          </div>
        </div>

        <div
          ref={statsRef}
          data-testid="hero-stats"
          style={{
            marginTop: 80,
            paddingTop: 28,
            borderTop: "1px solid var(--line)",
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
          }}
        >
          <style>{`
            @media (min-width: 768px) {
              [data-testid="hero-stats"] { grid-template-columns: repeat(4, 1fr) !important; }
            }
          `}</style>
          {STATS.map((s, i) => (
            <Stat key={s.value} s={s} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
